import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, LoaderCircle, Mail, Phone, Shield, Star } from "lucide-react";
import { usePlatformAuth } from "../../../lib/use-platform-auth";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { InlineMessage, InlineStatusBadge } from "../../../components/konto-helpers";

export function ProfilTab() {
  const { organizationSlug } = useParams<{ organizationSlug: string }>();
  const { profile, organizations, refresh } = usePlatformAuth();

  const currentOrganization = useMemo(
    () => organizations.find((o) => o.slug === organizationSlug) ?? null,
    [organizationSlug, organizations],
  );
  const hasMultipleOrganizations = organizations.length > 1;

  const [firstName, setFirstName] = useState(profile?.firstName ?? "");
  const [lastName, setLastName] = useState(profile?.lastName ?? "");
  const [saving, setSaving] = useState(false);
  const [savingDefaultOrganization, setSavingDefaultOrganization] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setFirstName(profile?.firstName ?? "");
    setLastName(profile?.lastName ?? "");
  }, [profile?.firstName, profile?.lastName]);

  const isCurrentOrganizationDefault =
    !!currentOrganization && currentOrganization.id === profile?.defaultOrganizationId;

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!firstName.trim()) {
      setError("Fornavn må fylles ut.");
      return;
    }

    setSaving(true);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const response = await fetch(`${api}/api/users/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim() }),
      });

      if (!response.ok) throw new Error();
      await refresh();
      setMessage("Profilen din er oppdatert.");
    } catch {
      setError("Vi klarte ikke å lagre endringene dine.");
    } finally {
      setSaving(false);
    }
  };

  const handleMakeDefaultOrganization = () => {
    if (!currentOrganization) {
      return;
    }

    setSavingDefaultOrganization(true);
    setError(null);
    setMessage(null);

    const saveDefaultOrganization = async () => {
      try {
        const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
        const response = await fetch(`${api}/api/users/profile`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            defaultOrganizationId: currentOrganization.id,
          }),
        });

        if (!response.ok) {
          throw new Error();
        }

        await refresh();
        setMessage("Denne organisasjonen er nå standard ved innlogging.");
      } catch {
        setError("Vi klarte ikke å lagre standardorganisasjonen.");
      } finally {
        setSavingDefaultOrganization(false);
      }
    };

    void saveDefaultOrganization();
  };

  return (
    <section
      className="rounded-3xl bg-white/50 p-8 backdrop-blur-sm animate-in fade-in slide-in-from-bottom-4 duration-500"
      style={{ border: "1px dashed var(--border)" }}
    >
      <div className="mb-10 flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-3">
          <h2
            className="text-2xl font-black text-[#1a2e35]"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Profil og tilgang
          </h2>
          <p className="text-base font-medium text-muted-foreground max-w-md leading-relaxed">
            Oppdater dine personopplysninger og se kontaktinformasjonen som er knyttet til brukeren.
          </p>
        </div>

        <div className="relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm ring-1 ring-border transition-all hover:shadow-md sm:min-w-[280px]">
          <div className="absolute right-[-10%] top-[-10%] h-24 w-24 rounded-full bg-accent/5 blur-2xl" />
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60">
            Aktiv organisasjon
          </p>
          <p className="mt-3 text-xl font-black text-[#1a2e35]">
            {currentOrganization?.name ?? "Ikke valgt"}
          </p>
          {currentOrganization ? (
            <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-accent/10 px-3 py-1 text-xs font-bold text-accent">
              <Shield className="h-3 w-3" />
              {currentOrganization.role}
            </div>
          ) : null}
          {hasMultipleOrganizations && currentOrganization ? (
            <div className="mt-5 space-y-3">
              {isCurrentOrganizationDefault ? (
                <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                  <Check className="h-3 w-3" />
                  Standardorganisasjon
                </div>
              ) : null}
              <Button
                type="button"
                variant={isCurrentOrganizationDefault ? "secondary" : "default"}
                onClick={handleMakeDefaultOrganization}
                disabled={savingDefaultOrganization || isCurrentOrganizationDefault}
                className="w-full rounded-xl px-4 py-5 text-sm font-bold"
              >
                {savingDefaultOrganization ? (
                  <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                ) : isCurrentOrganizationDefault ? (
                  <Check className="mr-2 h-4 w-4" />
                ) : (
                  <Star className="mr-2 h-4 w-4" />
                )}
                {isCurrentOrganizationDefault ? "Dette er standardorganisasjonen" : "Gjor denne til standard"}
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-5 max-w-lg">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="firstName">Fornavn</Label>
            <Input
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Navn"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Etternavn</Label>
            <Input
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Etternavn"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">E-post</Label>
          <div className="relative">
            <Input id="email" value={profile?.email ?? ""} disabled className="pr-32" />
            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
              <InlineStatusBadge ok={profile?.emailVerified === true}>
                {profile?.emailVerified ? "Verifisert" : "Ikke verifisert"}
              </InlineStatusBadge>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="phoneNumber">Mobilnummer</Label>
          <div className="relative">
            <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="phoneNumber"
              value={profile?.phoneNumber ?? ""}
              disabled
              className="pl-10 pr-32"
            />
            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
              <InlineStatusBadge ok={profile?.phoneNumberVerified === true}>
                {profile?.phoneNumberVerified ? "Verifisert" : "Ikke verifisert"}
              </InlineStatusBadge>
            </div>
          </div>
        </div>

        {error ? <InlineMessage tone="error">{error}</InlineMessage> : null}
        {message ? <InlineMessage tone="success">{message}</InlineMessage> : null}

        <Button
          type="submit"
          disabled={saving}
          className="rounded-xl px-8 py-6 text-base font-bold shadow-lg shadow-accent/20 transition-all hover:scale-105 active:scale-95"
        >
          {saving ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
          Lagre endringer
        </Button>
      </form>
    </section>
  );
}
